import React, { createContext, useContext, useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged, signInWithEmailAndPassword } from 'firebase/auth';
import { getFirestore, doc, getDoc, setDoc, deleteDoc, updateDoc, collection, query, where, limit, getDocs, enableNetwork, serverTimestamp } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useNavigation, useNavigationState } from '@react-navigation/native';
import { LoadingScreen } from './LoadingScreen';

const UserContext = createContext();

export function LoginPersistenceProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigation = useNavigation();
  const currentRoute = useNavigationState(state => state?.routes[state.index]?.name);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 3;
  const retryDelay = 1000; // 1 second
  const [devModeUser, setDevModeUser] = useState(() => {
    if (process.env.NODE_ENV === 'development') {
      return localStorage.getItem('devModeUser') || null;
    }
    return null;
  });
  
  const isDev = process.env.NODE_ENV === 'development';

  // Add network initialization
  useEffect(() => {
    const initializeFirestore = async () => {
      const db = getFirestore();
      try {
        await enableNetwork(db);
        console.log('✅ Firestore network enabled');
      } catch (error) {
        console.error('Error enabling Firestore network:', error);
      }
    };

    initializeFirestore();
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      try {
        if (firebaseUser) {
          const db = getFirestore();
          
          // Function to handle user data
          const handleUserData = async (retryCount = 0) => {
            try {
              const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
              
              if (userDoc.exists()) {
                const userData = userDoc.data();
                
                // Update last login timestamp
                try {
                  await updateDoc(doc(db, 'users', firebaseUser.uid), {
                    lastLogin: new Date()
                  });
                } catch (updateError) {
                  console.warn('Could not update last login time:', updateError);
                  // Continue anyway - this is not critical
                }
                
                setUser({ ...userData, uid: firebaseUser.uid });

                // Navigation logic
                if (userData.userType === 'admin') {
                  // Admins can access everything
                  if (currentRoute === 'Login' || currentRoute === 'Waitlist') {
                    navigation.navigate('Home');
                  }
                } else if (userData.status === 'active') {
                  // Active users can access the app
                  if (currentRoute === 'Login' || currentRoute === 'Waitlist') {
                    navigation.navigate('Home');
                  }
                } else {
                  // Any other status (suspended, undefined, etc.) goes to waitlist
                  if (currentRoute !== 'Waitlist') {
                    navigation.navigate('Waitlist');
                  }
                }
              } else {
                // If in development mode and offline, create a temporary user object
                if (isDev) {
                  const tempUserData = {
                    email: firebaseUser.email,
                    userType: 'admin', // For development testing
                    status: 'active',
                    createdAt: new Date(),
                    lastLogin: new Date()
                  };
                  setUser({ ...tempUserData, uid: firebaseUser.uid });
                  navigation.navigate('Home');
                  return;
                }

                // If user document doesn't exist, create it with suspended status
                const newUserData = {
                  email: firebaseUser.email,
                  userType: 'user',
                  status: 'suspended',
                  createdAt: new Date(),
                  lastLogin: new Date(),
                  isPublic: false,
                  registrationMessage: 'Welcome to Venue Report Card! We are currently in closed alpha. Your account has been added to our waitlist and will be activated soon.',
                };
                
                try {
                  await setDoc(doc(db, 'users', firebaseUser.uid), newUserData);
                  setUser({ ...newUserData, uid: firebaseUser.uid });
                  navigation.navigate('Waitlist');
                } catch (createError) {
                  console.error('Error creating user document:', createError);
                  if (isDev) {
                    // In development, continue with temporary data
                    setUser({ ...newUserData, uid: firebaseUser.uid });
                    navigation.navigate('Waitlist');
                  } else {
                    throw createError;
                  }
                }
              }
            } catch (error) {
              console.error('Error accessing user document:', error);
              if (error.message.includes('offline') && retryCount < maxRetries) {
                // Wait and retry
                await new Promise(resolve => setTimeout(resolve, retryDelay));
                return handleUserData(retryCount + 1);
              }
              if (isDev) {
                // In development, create a temporary admin user if offline
                const tempUserData = {
                  email: firebaseUser.email,
                  userType: 'admin',
                  status: 'active',
                  createdAt: new Date(),
                  lastLogin: new Date()
                };
                setUser({ ...tempUserData, uid: firebaseUser.uid });
                navigation.navigate('Home');
              } else {
                throw error;
              }
            }
          };

          await handleUserData();
        } else {
          setUser(null);
          // Don't automatically redirect to Login
          // Allow the user to stay on the Landing page
          // Only redirect if they're on a protected route
          const protectedRoutes = ['Profile', 'MyReports', 'Admin', 'AddVenue'];
          if (currentRoute && protectedRoutes.includes(currentRoute)) {
            navigation.navigate('Home');
          }
        }
      } catch (error) {
        console.error('Error in auth state change:', error);
        if (isDev) {
          // In development, don't let errors prevent the app from working
          console.warn('Development mode: Continuing despite error');
        } else {
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [navigation, currentRoute]);

  // Dev mode auto-login
  useEffect(() => {
    const handleDevModeAutoLogin = async () => {
      if (isDev && devModeUser) {
        try {
          const auth = getAuth();
          const password = process.env.REACT_APP_DEV_PASSWORD;
          if (!password) {
            throw new Error('Development password not found');
          }

          if (!auth.currentUser || auth.currentUser.email !== devModeUser) {
            await signInWithEmailAndPassword(auth, devModeUser, password);
          }
        } catch (error) {
          console.error('Dev mode auto-login error:', error);
          localStorage.removeItem('devModeUser');
          setDevModeUser(null);
        }
      }
    };

    handleDevModeAutoLogin();
  }, [devModeUser, isDev]);

  // Store devModeUser in localStorage
  useEffect(() => {
    if (isDev) {
      if (devModeUser) {
        localStorage.setItem('devModeUser', devModeUser);
      } else {
        localStorage.removeItem('devModeUser');
      }
    }
  }, [devModeUser, isDev]);


  // Validate invite code (read-only, no auth needed)
  // Checks both inviteUrls (admin-made) and invite_codes (user-made) collections
  const validateInviteCode = async (code) => {
    if (!code || !code.trim()) {
      return { valid: false, codeId: null, codeType: null, error: 'Invite code is required' };
    }
  
    const db = getFirestore();
    const trimmedCode = code.trim().toUpperCase();
  
    try {
      // First, check inviteUrls collection (admin-made URLs)
      const inviteUrlsQuery = query(
        collection(db, 'inviteUrls'),
        where('inviteCode', '==', trimmedCode),
        where('isActive', '==', true)
      );
  
      const inviteUrlsSnapshot = await getDocs(inviteUrlsQuery);
  
      if (!inviteUrlsSnapshot.empty) {
        const inviteUrlDoc = inviteUrlsSnapshot.docs[0];
        const inviteUrlData = inviteUrlDoc.data();
        const urlId = inviteUrlDoc.id;
  
        // Check if URL is expired by date
        if (inviteUrlData.expirationType === 'date' && inviteUrlData.expirationDate) {
          const expirationDate = inviteUrlData.expirationDate.toDate ? 
            inviteUrlData.expirationDate.toDate() : 
            new Date(inviteUrlData.expirationDate);
          
          if (new Date() > expirationDate) {
            return { valid: false, codeId: null, codeType: null, error: 'This invite link has expired' };
          }
        }
  
        // Check if URL has reached user limit
        if (inviteUrlData.expirationType === 'users' && inviteUrlData.maxUsers) {
          const usedCount = inviteUrlData.usedCount || 0;
          if (usedCount >= inviteUrlData.maxUsers) {
            return { valid: false, codeId: null, codeType: null, error: 'This invite link has reached its user limit' };
          }
        }

        // Code is valid (but not claimed yet - will be claimed after user creation)
        // Note: We don't check if user has already used it here because during validation,
        // the user might not be authenticated yet. That check happens in claimInviteCode.
        return { valid: true, codeId: urlId, codeType: 'inviteUrl', error: null };
      }
  
      // If not found in inviteUrls, check invite_codes collection (user-made codes)
      const inviteCodesQuery = query(
        collection(db, 'invite_codes'),
        where('code', '==', trimmedCode)
      );
  
      const inviteCodesSnapshot = await getDocs(inviteCodesQuery);
  
      if (inviteCodesSnapshot.empty) {
        return { valid: false, codeId: null, codeType: null, error: 'Invalid invite code' };
      }
  
      const inviteCodeDoc = inviteCodesSnapshot.docs[0];
      const inviteCodeData = inviteCodeDoc.data();
      const codeId = inviteCodeDoc.id;
  
      // Check if already used
      if (inviteCodeData.isUsed) {
        return { valid: false, codeId: null, codeType: null, error: 'This invite code has already been used' };
      }
  
      // Check if expired
      const expiresAt = inviteCodeData.expiresAt?.toDate?.() || new Date(0);
      const now = new Date();
      
      if (now > expiresAt) {
        return { valid: false, codeId: null, codeType: null, error: 'This invite code has expired' };
      }
  
      // Code is valid (but not claimed yet - will be claimed after user creation)
      return { valid: true, codeId, codeType: 'invite_code', error: null };
    } catch (error) {
      console.error('Error validating invite code:', error);
      return { valid: false, codeId: null, codeType: null, error: 'Error validating invite code. Please try again.' };
    }
  };

  // Claim invite code (requires authentication - called after user creation)
  // Handles both inviteUrls (admin-made) and invite_codes (user-made)
  const claimInviteCode = async (codeId, userId, codeType = null) => {
    if (!codeId || !userId) {
      return { success: false, error: 'Code ID and User ID are required' };
    }

    try {
      const db = getFirestore();
      const auth = getAuth();
      
      // Ensure user is authenticated
      if (!auth.currentUser) {
        return { success: false, error: 'User must be authenticated to claim invite code' };
      }

      // If codeType is provided, use it; otherwise try to determine from document existence
      let actualCodeType = codeType;
      if (!actualCodeType) {
        // Try to determine type by checking which collection has the document
        const inviteUrlDoc = await getDoc(doc(db, 'inviteUrls', codeId));
        if (inviteUrlDoc.exists()) {
          actualCodeType = 'inviteUrl';
        } else {
          const inviteCodeDoc = await getDoc(doc(db, 'invite_codes', codeId));
          if (inviteCodeDoc.exists()) {
            actualCodeType = 'invite_code';
          } else {
            return { success: false, error: 'Invite code not found' };
          }
        }
      }

      if (actualCodeType === 'inviteUrl') {
        // Handle inviteUrls collection (admin-made URLs)
        const inviteUrlDoc = await getDoc(doc(db, 'inviteUrls', codeId));
        
        if (!inviteUrlDoc.exists()) {
          return { success: false, error: 'Invite URL not found' };
        }

        const inviteUrlData = inviteUrlDoc.data();
        
        // Check if user already used this URL
        const usedBy = inviteUrlData.usedBy || [];
        if (usedBy.includes(userId)) {
          return { success: false, error: 'You have already used this invite link' };
        }

        // Check if URL has reached user limit (race condition protection)
        if (inviteUrlData.expirationType === 'users' && inviteUrlData.maxUsers) {
          const usedCount = inviteUrlData.usedCount || 0;
          if (usedCount >= inviteUrlData.maxUsers) {
            return { success: false, error: 'This invite link has reached its user limit' };
          }
        }

        // Update the invite URL: increment usedCount and add userId to usedBy
        await updateDoc(doc(db, 'inviteUrls', codeId), {
          usedCount: (inviteUrlData.usedCount || 0) + 1,
          usedBy: [...usedBy, userId],
          lastUsedAt: serverTimestamp()
        });

        return { success: true, error: null };
      } else {
        // Handle invite_codes collection (user-made codes)
        const inviteCodeDoc = await getDoc(doc(db, 'invite_codes', codeId));
        
        if (!inviteCodeDoc.exists()) {
          return { success: false, error: 'Invite code not found' };
        }

        const inviteCodeData = inviteCodeDoc.data();
        
        // Double-check it's not already used (race condition protection)
        if (inviteCodeData.isUsed) {
          return { success: false, error: 'This invite code has already been used' };
        }

        // Claim the code (user is now authenticated, so security rules allow this)
        await updateDoc(doc(db, 'invite_codes', codeId), {
          isUsed: true,
          usedAt: serverTimestamp(),
          usedBy: userId
        });

        return { success: true, error: null };
      }
    } catch (error) {
      console.error('Error claiming invite code:', error);
      return { success: false, error: 'Error claiming invite code. Please try again.' };
    }
  };


  const linkUserToInviteCode = async (codeId, userId) => {
    if (!codeId || !userId) return;
  
    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'invite_codes', codeId), {
        usedBy: userId
      });
    } catch (error) {
      console.error('Error linking user to invite code:', error);
      // Non-critical error, don't throw
    }
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <UserContext.Provider value={{
      user,
      setUser,
      loading,
      devModeUser,
      setDevModeUser,
      linkUserToInviteCode,
      validateInviteCode,
      claimInviteCode
    }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

